<template>
  <!-- can-cancel = ['escape', 'x', 'outside'] si se quiere que cierre  por cualqiera de esos [] caso contrario -->
  <b-modal :active.sync="isModalActive" :width="680" scroll="keep" :can-cancel="[]">
    <b-loading :is-full-page="false" v-model="isLoading" :can-cancel="false"></b-loading>
    <div class="rounded-lg bg-white overflow-hidden border border-[#E4E6EB] shadow-lg">
      <!-- HEADER estilo Meta -->
      <header class="flex justify-between items-center px-4 py-3 border-b border-[#E4E6EB] bg-white">
        <div class="flex-1 min-w-0">
          <h2 class="text-base font-semibold text-gray-900 break-words whitespace-normal">
            {{ title }}
          </h2>
        </div>
        <button
          v-if="!processingData"
          @click="isModalActive = false"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1"
          aria-label="Cerrar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </header>

      <!-- CONTENIDO -->
      <div class="px-4 py-4 space-y-4 bg-white">
        <div>
          <b-field
            :label="'Nombre del Indicador *'"
            class="w-full lock text-sm font-medium mb-2 text-gray-400"
            :type="errors.name ? 'is-danger' : ''"
            :message="errors.name || ''">
            <b-input
              v-model="name"
              type="text"
              placeholder="Ej. Calidad de Obra"
              @input="errors.name = ''" />
          </b-field>
        </div>

        <div>
          <b-field label="Descripción" class="w-full lock text-sm font-medium mb-2 text-gray-400">
            <b-input
              v-model="description"
              type="text"
              placeholder="Ej. Mide la calidad general de la obra" />
          </b-field>
        </div>

        <div class="flex items-center space-x-4 w-full">
          <b-field label="Seleccione un color" class="w-full">
            <b-colorpicker
                v-model="color"
                :color-formatter="formatter"
                :alpha="hasAlpha"
                expanded
            />
        </b-field>
        </div>

        <hr class="border-gray-700">

        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <span class="dot-numerador"></span>
            <span class="text-sm font-bold text-gray-700 uppercase tracking-widest">Numerador</span>
          </div>

          <div class="grid grid-cols-12 gap-3 items-center">
            <div class="col-span-6">
              <label class="text-xs text-gray-400 font-medium tracking-wider mb-1 block">
                {{ $t('statuses') }} <span class="required-star">*</span>
              </label>

              <div :class="{ 'multiselect-error': errors.numeratorStatus }">
                <multiselect
                  v-model="numeratorStatus"
                  :options="statuses"
                  :multiple="false"
                  :hide-selected="false"
                  track-by="name"
                  :customLabel="_selectedLabel"
                  :show-labels="false"
                  :placeholder="$t('select')"
                  :max-height="180"
                  open-direction="top"
                  @input="errors.numeratorStatus = ''"
                  >

                  <!-- Template de las opciones -->
                  <template slot="option" slot-scope="{ option }">
                    <div class="flex items-center space-x-2">
                      <span
                        class="inline-block w-4 h-4 rounded"
                        :style="{ backgroundColor: option.color }"
                      ></span>
                      <span>{{ option.name }}</span>
                    </div>
                  </template>

                  <template slot="clear" slot-scope="props">
                    <div class="multiselect__clear" v-if="statuses.length" @mousedown.prevent.stop="clearAll(props.search)"></div>
                  </template>

                  <!-- Template del valor seleccionado -->
                  <template slot="singleLabel" slot-scope="{ option }">
                    <div class="flex items-center space-x-2">
                      <span
                        class="inline-block w-4 h-4 rounded"
                        :style="{ backgroundColor: option.color }"
                      ></span>
                      <span>{{ option.name }}</span>
                    </div>
                  </template>

                </multiselect>
              </div>
              <!-- <span v-if="errors.numeratorStatus" class="error-msg">{{ errors.numeratorStatus }}</span> -->
            </div>

            <div class="col-span-6">
              <label class="text-xs text-gray-400 font-medium tracking-wider mb-1 block">
                {{ $t('entities') }} <span class="required-star">*</span>
              </label>

              <div :class="{ 'multiselect-error': errors.numeratorEntity }">
                <multiselect
                  v-model="numeratorEntity"
                  :options="entityOptions"
                  :multiple="false"
                  :hide-selected="false"
                  track-by="value"
                  :customLabel="_selectedLabel"
                  :show-labels="false"
                  :placeholder="$t('select')"
                  :max-height="180"
                  open-direction="top"
                  @input="errors.numeratorEntity = ''"
                />
              </div>
              <!-- <span v-if="errors.numeratorEntity" class="error-msg">{{ errors.numeratorEntity }}</span> -->
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 py-2">
          <div class="flex-1 h-px bg-gradient-to-r from-transparent via-gray-400 to-gray-400"></div>
          <span class="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 border border-gray-300 text-md font-bold text-gray-500 select-none shadow-sm">÷</span>
          <div class="flex-1 h-px bg-gradient-to-l from-transparent via-gray-400 to-gray-400"></div>
        </div>

        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <span class="dot-denominador"></span>
            <span class="text-sm font-bold text-gray-700 uppercase tracking-widest">Denominador</span>
          </div>
          <div class="grid grid-cols-12 gap-3 items-center">
            <div class="col-span-6">
              <label class="text-xs text-gray-400 font-medium tracking-wider mb-1 block">
                {{ $t('statuses') }} <span class="required-star">*</span>
              </label>

              <div :class="{ 'multiselect-error': errors.denominatorStatus }">
                <multiselect
                  v-model="denominatorStatus"
                  :options="statuses"
                  :multiple="false"
                  :hide-selected="false"
                  track-by="name"
                  :customLabel="_selectedLabel"
                  :show-labels="false"
                  :placeholder="$t('select')"
                  :max-height="180"
                  open-direction="top"
                  @input="errors.denominatorStatus = ''"
                  >

                  <!-- Template de las opciones -->
                  <template slot="option" slot-scope="{ option }">
                    <div class="flex items-center space-x-2">
                      <span
                        class="inline-block w-4 h-4 rounded"
                        :style="{ backgroundColor: option.color }"
                      ></span>
                      <span>{{ option.name }}</span>
                    </div>
                  </template>

                  <template slot="clear" slot-scope="props">
                    <div class="multiselect__clear" v-if="statuses.length" @mousedown.prevent.stop="clearAll(props.search)"></div>
                  </template>

                  <!-- Template del valor seleccionado -->
                  <template slot="singleLabel" slot-scope="{ option }">
                    <div class="flex items-center space-x-2">
                      <span
                        class="inline-block w-4 h-4 rounded"
                        :style="{ backgroundColor: option.color }"
                      ></span>
                      <span>{{ option.name }}</span>
                    </div>
                  </template>

                </multiselect>
              </div>
              <!-- <span v-if="errors.denominatorStatus" class="error-msg">{{ errors.denominatorStatus }}</span> -->
            </div>
            <div class="col-span-6">
              <label class="text-xs text-gray-400 font-medium tracking-wider mb-1 block">
                {{ $t('entities') }} <span class="required-star">*</span>
              </label>

              <div :class="{ 'multiselect-error': errors.denominatorEntity }">
                <multiselect
                  v-model="denominatorEntity"
                  :options="entityOptions"
                  :multiple="false"
                  :hide-selected="false"
                  track-by="value"
                  :customLabel="_selectedLabel"
                  :show-labels="false"
                  :placeholder="$t('select')"
                  :max-height="180"
                  open-direction="top"
                  @input="errors.denominatorEntity = ''"
                />
              </div>
              <!-- <span v-if="errors.denominatorEntity" class="error-msg">{{ errors.denominatorEntity }}</span> -->
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER estilo Meta -->
      <footer class="px-4 py-3 border-t border-[#E4E6EB] bg-white">
        <div class="w-full flex items-center gap-2" :class="processingData ? 'justify-center' : 'justify-between'">
          <v-button v-if="!processingData" color="grey" icon="door-closed" size="is-small" @click="isModalActive = false">{{ $t('close') }}</v-button>
          <v-button v-if="!processingData" @click="handleClick()" icon="plus" size="is-small">
            {{ data ? $t('save') : 'Ejecutar acción' }}
          </v-button>
          <v-button-processing v-if="processingData" />
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>

import Multiselect from 'vue-multiselect'

export default {
  name: "RatioModal",

  components: {
    Multiselect
  },

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      processingData: false,

      data: null,
      title: '',
      statuses: [],

      id: null,
      name: '',
      description: '',
      numeratorStatus: null,
      numeratorEntity: null,
      denominatorStatus: null,
      denominatorEntity: null,
      entityOptions: [
        {
          value: 'collaborator',
          name: 'Colaboradores'
        },
        {
          value: 'supervisor',
          name: 'Supervisores'
        }
      ],

      // colorpicker
      formatColor: "hex",
      color: "#3e8ed0",

      errors: {
        name: '',
        numeratorStatus: '',
        numeratorEntity: '',
        denominatorStatus: '',
        denominatorEntity: '',
      },
    }
  },

  computed: {
    hasAlpha() {
        return /a\s*$/i.test(this.formatColor);
    },
  },

  methods: {
    _selectedLabel(value) {
      return value.name
    },

    clearAll() {
      this.numeratorStatus = '';
      this.numeratorEntity = '';
      this.denominatorStatus = '';
      this.denominatorEntity = '';
    },

    validate() {
      let valid = true;
      this.errors = { name: '', numeratorStatus: '', numeratorEntity: '', denominatorStatus: '', denominatorEntity: '' };

      if (!this.name || !this.name.trim()) {
        this.errors.name = 'El nombre es requerido';
        valid = false;
      }
      if (!this.numeratorStatus) {
        this.errors.numeratorStatus = 'Requerido';
        valid = false;
      }
      if (!this.numeratorEntity) {
        this.errors.numeratorEntity = 'Requerido';
        valid = false;
      }
      if (!this.denominatorStatus) {
        this.errors.denominatorStatus = 'Requerido';
        valid = false;
      }
      if (!this.denominatorEntity) {
        this.errors.denominatorEntity = 'Requerido';
        valid = false;
      }

      if (!valid) {
        this.$toast.open({
          message: 'Por favor, complete todos los campos requeridos correctamente',
          type: 'is-danger',
          position: 'is-top-right',
        });
      }

      return valid;
    },

    async handleClick() {
      if (!this.validate()) return;

      this.processingData = true;

      const data = {
        id: this.id,
        name: this.name,
        description: this.description,
        color: typeof this.color === 'string' ? this.color : this.formatter(this.color),
        formula: {
          numerator: {
            statusId: this.numeratorStatus.id,
            entityId: this.numeratorEntity.value,
            entityType: 'group',
          },
          denominator: {
            statusId: this.denominatorStatus.id,
            entityId: this.denominatorEntity.value,
            entityType: 'group',
          }
        }
      };

      this.processingData = false;

      this.$emit('epc-save-ratio', data);
      this.hide();
    },

    formatter(color) {
      return color.toString(this.formatColor);
    },

    show(data) {
      this.errors = { name: '', numeratorStatus: '', numeratorEntity: '', denominatorStatus: '', denominatorEntity: '' };
      this.data = data;

      this.title = data.title;
      this.statuses = data.statuses;

      this.id = data.ratio.id ?? null;
      this.name = data.ratio.name ?? '';
      this.description = data.ratio.description ?? '';
      this.color = data.ratio.color ?? '#3e8ed0';
      this.numeratorStatus = data.ratio.formula.numerator.statusId ? this.statuses.find(status => status.id === data.ratio.formula.numerator.statusId) : null;
      this.numeratorEntity = data.ratio.formula.numerator.entityId ? this.entityOptions.find(entity => entity.value === data.ratio.formula.numerator.entityId) : null;
      this.denominatorStatus = data.ratio.formula.denominator.statusId ? this.statuses.find(status => status.id === data.ratio.formula.denominator.statusId) : null;
      this.denominatorEntity = data.ratio.formula.denominator.entityId ? this.entityOptions.find(entity => entity.value === data.ratio.formula.denominator.entityId) : null;

      this.isModalActive = true;
    },

    hide() {
      this.data = null;

      this.id = '';
      this.name = '';
      this.description = '';
      this.numeratorStatus = null;
      this.numeratorEntity = null;
      this.denominatorStatus = null;
      this.denominatorEntity = null;
      this.color = '#3e8ed0';
      this.errors = { name: '', numeratorStatus: '', numeratorEntity: '', denominatorStatus: '', denominatorEntity: '' };

      this.isModalActive = false;

    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
  /* Oculta los botones para aumentar los inputs numéricos */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
    appearance: textfield; /* Estándar */
  }

  /* Scrollbar personalizado estilo Material Design 3 */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  .dot-numerador,
  .dot-denominador {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .dot-numerador {
    background-color: #60a5fa; /* blue-400 */
  }

  .dot-denominador {
    background-color: #fb923c; /* orange-400 */
  }

  .required-star {
    color: #ef4444;
    font-weight: 700;
  }

  .error-msg {
    display: block;
    font-size: 11px;
    color: #ef4444;
    margin-top: 4px;
  }

  .multiselect-error::v-deep .multiselect__tags {
    border-color: #ef4444;
  }
</style>
